import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Flame, Bell, BellOff, X } from 'lucide-react';
import axios from 'axios';
import { logError } from '../lib/errorLogger';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const StreakReminderBanner = ({ streak }) => {
  const [dismissed, setDismissed] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    setPushSupported('serviceWorker' in navigator && 'PushManager' in window);
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.pushManager.getSubscription().then((sub) => {
          setPushEnabled(!!sub);
        });
      });
    }
  }, []);

  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return null;
    return navigator.serviceWorker.register('/sw.js');
  }, []);

  const subscribeToPush = useCallback(async () => {
    setSubscribing(true);
    try {
      await registerServiceWorker();
      const reg = await navigator.serviceWorker.ready;

      const vapidRes = await axios.get(`${API}/push/vapid-key`);
      const vapidKey = vapidRes.data.public_key;
      if (!vapidKey) return;

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      const subJson = subscription.toJSON();
      const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        timezone: userTz,
      }, { withCredentials: true });

      setPushEnabled(true);
      toast.success('Streak reminders enabled! We\'ll notify you at 7 PM.');
    } catch (err) {
      logError('StreakReminderBanner', 'Push subscription failed', { error: err?.message });
      toast.error('Could not enable notifications. Check browser permissions.');
    } finally {
      setSubscribing(false);
    }
  }, [registerServiceWorker]);

  const unsubscribeFromPush = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
      await axios.delete(`${API}/push/subscribe`, { withCredentials: true });
      setPushEnabled(false);
      toast.success('Streak reminders disabled.');
    } catch (err) {
      logError('StreakReminderBanner', 'Push unsubscribe failed', { error: err?.message });
    }
  }, []);

  if (!streak || dismissed) return null;

  // In-app banner: show if user hasn't logged today and has an active streak
  const showBanner = !streak.logged_today && streak.current_streak >= 2;

  return (
    <div className="space-y-3" data-testid="streak-reminder">
      {/* In-app streak-at-risk banner */}
      {showBanner && (
        <div className="bg-gradient-to-r from-[#A388EE] to-[#d4836b] rounded-xl p-4 flex items-center justify-between text-white animate-fade-in">
          <div className="flex items-center gap-3">
            <Flame className="w-6 h-6 flex-shrink-0" />
            <div>
              <p className="font-semibold text-sm">
                Don't break your {streak.current_streak}-day streak!
              </p>
              <p className="text-xs text-white/80">Log today to keep your progress going.</p>
            </div>
          </div>
          <button onClick={() => setDismissed(true)} className="text-white/70 hover:text-white p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Push notification opt-in */}
      {pushSupported && (
        <div className="flex items-center justify-between bg-[#FFF2EC] rounded-xl px-4 py-3">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#FF6B4A]" />
            <span className="text-xs text-[#8C7A76]">
              {pushEnabled ? 'Streak reminders enabled' : 'Get streak reminders'}
            </span>
          </div>
          {pushEnabled ? (
            <Button size="sm" variant="ghost" onClick={unsubscribeFromPush}
              className="text-xs text-[#8C7A76] h-7 px-3" data-testid="push-disable-btn">
              <BellOff className="w-3 h-3 mr-1" /> Disable
            </Button>
          ) : (
            <Button size="sm" onClick={subscribeToPush} disabled={subscribing}
              className="bg-[#FF6B4A] hover:bg-[#E8552F] text-xs h-7 px-3 rounded-full" data-testid="push-enable-btn">
              <Bell className="w-3 h-3 mr-1" /> {subscribing ? 'Enabling...' : 'Enable'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default StreakReminderBanner;
